/**
 * Lista de productos — mismo layout que desktop, optimizada para catálogos
 * grandes:
 *   - `useInfiniteQuery` con cursor: trae 100 productos por página.
 *   - Virtualización con `@tanstack/react-virtual`: solo renderiza las
 *     filas visibles aunque haya miles cargadas.
 *   - `placeholderData: keepPreviousData`: al cambiar filtros no se ve
 *     el flash "Cargando…", la data anterior se mantiene visible.
 *   - `products.count` para el contador del header (COUNT indexado, no
 *     trae rows).
 *
 * La preferencia Cards/Lista se persiste en localStorage; el filtro de
 * categoría vive en la URL (`?categoryId=...`).
 */

import { Button, Card, CardContent, CardHeader, CardTitle, cn } from '@atreus/ui';
import { keepPreviousData } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  ImageOff,
  LayoutGrid,
  List,
  Pencil,
  Plus,
  Search,
  Trash2,
  X,
} from 'lucide-react';
import {
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { resolveImageUrl } from '../components/ImageDropzone.js';
import { ImageLightbox } from '../components/ImageLightbox.js';
import { CardsSkeleton, RowsSkeleton } from '../components/skeletons.js';
import { formatCOP } from '../lib/format.js';
import { usePermissions } from '../lib/permissions.js';
import { trpc } from '../lib/trpc.js';

type ViewMode = 'cards' | 'list';
const VIEW_STORAGE_KEY = 'atreus.pos.products.view';
const PAGE_SIZE = 100;

const loadInitialView = (): ViewMode => {
  if (typeof window === 'undefined') return 'cards';
  const stored = window.localStorage.getItem(VIEW_STORAGE_KEY);
  return stored === 'list' ? 'list' : 'cards';
};

/**
 * Hook chiquito: columnas en cards según window width. Matchea las
 * breakpoints Tailwind de la grilla (`sm:2 / lg:3 / xl:4`).
 */
const useCardColumns = (): number => {
  const [cols, setCols] = useState<number>(() => {
    if (typeof window === 'undefined') return 4;
    return computeCols(window.innerWidth);
  });
  useEffect(() => {
    const onResize = (): void => setCols(computeCols(window.innerWidth));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return cols;
};

const computeCols = (w: number): number => {
  if (w >= 1280) return 4;
  if (w >= 1024) return 3;
  if (w >= 640) return 2;
  return 1;
};

export const ProductsPage = () => {
  const [search, setSearch] = useState('');
  // `useDeferredValue` separa el input (urgente) del query (no urgente).
  // Al tipear rápido en un catálogo grande, el input sigue respondiendo
  // mientras React posterga el refetch hasta que el usuario hace pausa.
  const deferredSearch = useDeferredValue(search);
  const [view, setView] = useState<ViewMode>(loadInitialView);
  const [lightbox, setLightbox] = useState<{ src: string; alt: string } | null>(
    null,
  );

  const openLightbox = (url: string | null, alt: string): void => {
    const resolved = resolveImageUrl(url);
    if (!resolved) return;
    setLightbox({ src: resolved, alt });
  };

  const navigate = useNavigate();
  // strict:false: el componente se monta en tests con routers memoria que
  // no tienen la ruta tipada.
  const urlSearch = useSearch({ strict: false }) as { categoryId?: string };
  const categoryFilter = urlSearch.categoryId ?? null;
  const setCategoryFilter = (categoryId: string | null): void => {
    void navigate({
      to: '/products',
      search: categoryId ? { categoryId } : {},
    });
  };

  const queryInput = useMemo(() => {
    const out: { search?: string; categoryId?: string; limit: number } = {
      limit: PAGE_SIZE,
    };
    const s = deferredSearch.trim();
    if (s) out.search = s;
    if (categoryFilter) out.categoryId = categoryFilter;
    return out;
  }, [deferredSearch, categoryFilter]);

  const listQuery = trpc.products.list.useInfiniteQuery(queryInput, {
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    placeholderData: keepPreviousData,
  });

  const countQuery = trpc.products.count.useQuery(
    {
      ...(queryInput.search ? { search: queryInput.search } : {}),
      ...(queryInput.categoryId ? { categoryId: queryInput.categoryId } : {}),
    },
    { placeholderData: keepPreviousData },
  );

  const items = useMemo(
    () => listQuery.data?.pages.flatMap((p) => p.items) ?? [],
    [listQuery.data],
  );

  const categories = trpc.categories.list.useQuery();
  const utils = trpc.useUtils();
  const del = trpc.products.delete.useMutation({
    onSuccess: () => {
      void utils.products.list.invalidate();
      void utils.products.count.invalidate();
    },
  });
  const permissions = usePermissions();
  const canCreate = permissions.canDo('products.create');
  const canEdit = permissions.canDo('products.edit');
  const canDelete = permissions.canDo('products.delete');

  useEffect(() => {
    window.localStorage.setItem(VIEW_STORAGE_KEY, view);
  }, [view]);

  const categoryMap = useMemo(
    () => new Map(categories.data?.map((c) => [c.id, c.name]) ?? []),
    [categories.data],
  );

  const handleDelete = (id: string, name: string): void => {
    if (!window.confirm(`¿Eliminar "${name}"?`)) return;
    del.mutate({ id });
  };

  const totalCount = countQuery.data?.total ?? 0;
  const loadedCount = items.length;
  const isLoadingFirstPage = listQuery.isLoading;
  const isEmpty = !isLoadingFirstPage && items.length === 0;

  return (
    <div className="flex h-full flex-col">
      <header className="flex items-center justify-between border-b border-border bg-surface px-8 py-5">
        <div>
          <h1 className="text-2xl font-black tracking-tight">Productos</h1>
          <p className="text-sm text-fg-muted">
            {totalCount === 0
              ? '0 elementos'
              : totalCount === 1
                ? '1 elemento'
                : `${totalCount} elementos${loadedCount < totalCount ? ` · ${loadedCount} cargados` : ''}`}
          </p>
        </div>
        {canCreate ? (
          <Button asChild size="lg">
            <Link to="/products/new">
              <Plus className="size-4" /> Nuevo producto
            </Link>
          </Button>
        ) : null}
      </header>

      <div className="flex items-center justify-between gap-3 border-b border-border bg-surface px-8 py-3">
        <div className="flex flex-1 items-center gap-3">
          <div className="relative max-w-md flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-fg-subtle" />
            <input
              type="search"
              placeholder="Buscar por nombre o SKU…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-md border border-border bg-surface px-9 py-2 text-sm outline-none placeholder:text-fg-subtle focus:border-fg"
            />
          </div>
          {categoryFilter ? (
            <button
              type="button"
              onClick={() => setCategoryFilter(null)}
              className="flex items-center gap-1.5 rounded-full border border-fg bg-fg px-3 py-1 text-xs font-semibold text-fg-inverse hover:bg-fg-muted"
              title="Quitar filtro de categoría"
            >
              {categoryMap.get(categoryFilter) ?? 'Categoría'}
              <X className="size-3" />
            </button>
          ) : null}
        </div>
        <ViewToggle value={view} onChange={setView} />
      </div>

      <div className="flex-1 overflow-hidden">
        {del.error ? (
          <Card className="m-4 border-danger/50 bg-danger/10">
            <CardContent className="p-3 text-sm text-danger">
              {del.error.message}
            </CardContent>
          </Card>
        ) : null}
        {isLoadingFirstPage ? (
          <div className="p-8">
            {view === 'list' ? (
              <RowsSkeleton count={10} />
            ) : (
              <CardsSkeleton count={9} />
            )}
          </div>
        ) : isEmpty ? (
          <EmptyState hasSearch={!!deferredSearch.trim()} />
        ) : (
          <VirtualizedBody
            view={view}
            items={items}
            categoryMap={categoryMap}
            onDelete={handleDelete}
            deleting={del.isPending}
            onFilterCategory={setCategoryFilter}
            onImageClick={openLightbox}
            canEdit={canEdit}
            canDelete={canDelete}
            hasNextPage={listQuery.hasNextPage}
            isFetchingNextPage={listQuery.isFetchingNextPage}
            fetchNextPage={listQuery.fetchNextPage}
          />
        )}
      </div>

      {lightbox ? (
        <ImageLightbox
          src={lightbox.src}
          alt={lightbox.alt}
          onClose={() => setLightbox(null)}
        />
      ) : null}
    </div>
  );
};

// ---- Empty state ----

const EmptyState = ({ hasSearch }: { hasSearch: boolean }) => (
  <div className="p-8">
    <Card className="mx-auto max-w-md">
      <CardContent className="p-8 text-center">
        <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-surface-muted text-fg-muted">
          <Search className="size-5" />
        </div>
        <h2 className="mt-4 text-lg font-bold tracking-tight">
          {hasSearch ? 'Sin resultados' : 'Aún no hay productos'}
        </h2>
        <p className="mt-1 text-sm text-fg-muted">
          {hasSearch
            ? 'Prueba con otro término de búsqueda.'
            : 'Crea tu primer producto para empezar.'}
        </p>
        {!hasSearch ? (
          <Button asChild className="mt-4">
            <Link to="/products/new">
              <Plus className="size-4" /> Crear producto
            </Link>
          </Button>
        ) : null}
      </CardContent>
    </Card>
  </div>
);

// ---- Virtualized body ----

interface VirtualizedBodyProps {
  view: ViewMode;
  items: readonly ProductRow[];
  categoryMap: Map<string, string>;
  onDelete: (id: string, name: string) => void;
  deleting: boolean;
  onFilterCategory: (categoryId: string) => void;
  onImageClick: (url: string | null, alt: string) => void;
  canEdit: boolean;
  canDelete: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
}

const VirtualizedBody = ({
  view,
  items,
  categoryMap,
  onDelete,
  deleting,
  onFilterCategory,
  onImageClick,
  canEdit,
  canDelete,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
}: VirtualizedBodyProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const cols = useCardColumns();
  const effectiveCols = view === 'list' ? 1 : cols;
  const rowCount = Math.ceil(items.length / effectiveCols);
  // +1 fila sentinela al final cuando hay más páginas — al scrollear dentro
  // de ella se dispara `fetchNextPage`.
  const virtualRowCount = hasNextPage ? rowCount + 1 : rowCount;

  const estimateSize = (): number => (view === 'list' ? 56 : 360);

  const virtualizer = useVirtualizer({
    count: virtualRowCount,
    getScrollElement: () => scrollRef.current,
    estimateSize,
    overscan: 4,
  });

  // Trigger `fetchNextPage` cuando la fila sentinela entra en viewport.
  const virtualItems = virtualizer.getVirtualItems();
  useEffect(() => {
    const last = virtualItems[virtualItems.length - 1];
    if (!last) return;
    if (last.index >= rowCount && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [virtualItems, rowCount, hasNextPage, isFetchingNextPage, fetchNextPage]);

  if (view === 'list') {
    return (
      <div ref={scrollRef} className="h-full overflow-y-auto px-8 pb-8 pt-4">
        <div className="overflow-hidden rounded-md border border-border bg-surface">
          <div className="sticky top-0 z-10 flex items-center gap-3 border-b border-border bg-surface-muted px-3 py-2 text-left text-xs uppercase tracking-wider text-fg-muted">
            <div className="size-10 shrink-0" aria-hidden="true" />
            <div className="flex-1 font-semibold">Nombre</div>
            <div className="w-24 font-semibold">SKU</div>
            <div className="w-32 font-semibold">Categoría</div>
            <div className="w-24 text-right font-semibold">Precio</div>
            {canEdit || canDelete ? <div className="w-24" aria-hidden="true" /> : null}
          </div>
          <div
            style={{
              height: virtualizer.getTotalSize(),
              position: 'relative',
            }}
          >
            {virtualItems.map((vRow) => {
              const isLoaderRow = vRow.index >= rowCount;
              const item = items[vRow.index];
              return (
                <div
                  key={vRow.key}
                  data-index={vRow.index}
                  ref={virtualizer.measureElement}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    transform: `translateY(${vRow.start}px)`,
                  }}
                >
                  {isLoaderRow ? (
                    <LoaderRow />
                  ) : item ? (
                    <ListRow
                      p={item}
                      categoryMap={categoryMap}
                      onDelete={onDelete}
                      deleting={deleting}
                      onFilterCategory={onFilterCategory}
                      onImageClick={onImageClick}
                      canEdit={canEdit}
                      canDelete={canDelete}
                    />
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
        {isFetchingNextPage ? <InlineSpinner /> : null}
      </div>
    );
  }

  // --- Cards ---
  return (
    <div ref={scrollRef} className="h-full overflow-y-auto p-8">
      <div
        style={{
          height: virtualizer.getTotalSize(),
          position: 'relative',
          width: '100%',
        }}
      >
        {virtualItems.map((vRow) => {
          const isLoaderRow = vRow.index >= rowCount;
          const start = vRow.index * effectiveCols;
          const rowItems = items.slice(start, start + effectiveCols);
          return (
            <div
              key={vRow.key}
              data-index={vRow.index}
              ref={virtualizer.measureElement}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${vRow.start}px)`,
                padding: '0 0 1rem 0',
              }}
            >
              {isLoaderRow ? (
                <LoaderRow />
              ) : (
                <div
                  className="grid gap-4"
                  style={{
                    gridTemplateColumns: `repeat(${effectiveCols}, minmax(0, 1fr))`,
                  }}
                >
                  {rowItems.map((p) => (
                    <CardTile
                      key={p.id}
                      p={p}
                      categoryMap={categoryMap}
                      onDelete={onDelete}
                      deleting={deleting}
                      onFilterCategory={onFilterCategory}
                      onImageClick={onImageClick}
                      canEdit={canEdit}
                      canDelete={canDelete}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {isFetchingNextPage ? <InlineSpinner /> : null}
    </div>
  );
};

const LoaderRow = () => (
  <div className="flex items-center justify-center py-6 text-sm text-fg-muted">
    Cargando más…
  </div>
);

const InlineSpinner = () => (
  <div className="py-4 text-center text-xs text-fg-muted">Cargando más…</div>
);

// ---- Tipos ----

interface ProductRow {
  id: string;
  name: string;
  sku: string;
  priceCents: number;
  imageUrl: string | null;
  categoryId: string | null;
  active: boolean;
}

interface RowProps {
  p: ProductRow;
  categoryMap: Map<string, string>;
  onDelete: (id: string, name: string) => void;
  deleting: boolean;
  onFilterCategory: (categoryId: string) => void;
  onImageClick: (url: string | null, alt: string) => void;
  canEdit: boolean;
  canDelete: boolean;
}

// ---- CardTile ----

const CardTile = ({
  p,
  categoryMap,
  onDelete,
  deleting,
  onFilterCategory,
  onImageClick,
  canEdit,
  canDelete,
}: RowProps) => (
  <Card
    className={cn('flex flex-col overflow-hidden', !p.active && 'opacity-60')}
  >
    <ClickableThumbnail
      url={p.imageUrl}
      alt={p.name}
      className="h-40 w-full"
      onClick={() => onImageClick(p.imageUrl, p.name)}
    />
    <CardHeader>
      <div className="flex items-start justify-between gap-2">
        <CardTitle className="line-clamp-2">{p.name}</CardTitle>
        {!p.active ? (
          <span className="rounded border border-border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-fg-muted">
            Inactivo
          </span>
        ) : null}
      </div>
      <div className="font-mono text-xs text-fg-subtle">{p.sku}</div>
    </CardHeader>
    <CardContent className="flex flex-1 flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <div className="text-2xl font-black tracking-tight">
          {formatCOP(p.priceCents)}
        </div>
      </div>
      {p.categoryId ? (
        <button
          type="button"
          onClick={() => onFilterCategory(p.categoryId!)}
          className="self-start text-xs uppercase tracking-wider text-fg-muted hover:text-fg hover:underline"
          title={`Filtrar por "${categoryMap.get(p.categoryId) ?? 'categoría'}"`}
        >
          {categoryMap.get(p.categoryId) ?? 'Sin categoría'}
        </button>
      ) : null}
      {canEdit || canDelete ? (
        <div className="mt-auto flex gap-2 pt-2">
          {canEdit ? (
            <Button asChild variant="outline" size="sm" className="flex-1">
              <Link to="/products/$productId" params={{ productId: p.id }}>
                <Pencil className="size-3.5" /> Editar
              </Link>
            </Button>
          ) : null}
          {canDelete ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDelete(p.id, p.name)}
              disabled={deleting}
            >
              <Trash2 className="size-3.5" />
            </Button>
          ) : null}
        </div>
      ) : null}
    </CardContent>
  </Card>
);

// ---- ListRow ----

const ListRow = ({
  p,
  categoryMap,
  onDelete,
  deleting,
  onFilterCategory,
  onImageClick,
  canEdit,
  canDelete,
}: RowProps) => (
  <div
    className={cn(
      'flex items-center gap-3 border-t border-border px-3 py-2 text-sm transition-colors hover:bg-surface-muted',
      !p.active && 'opacity-60',
    )}
  >
    <ClickableThumbnail
      url={p.imageUrl}
      alt={p.name}
      className="size-10 shrink-0 rounded"
      onClick={() => onImageClick(p.imageUrl, p.name)}
    />
    <div className="flex-1 truncate font-medium">
      {p.name}
      {!p.active ? (
        <span className="ml-2 rounded border border-border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-fg-muted">
          Inactivo
        </span>
      ) : null}
    </div>
    <div className="w-24 truncate font-mono text-xs text-fg-subtle">
      {p.sku}
    </div>
    <div className="w-32 truncate text-xs uppercase tracking-wider text-fg-muted">
      {p.categoryId ? (
        <button
          type="button"
          onClick={() => onFilterCategory(p.categoryId!)}
          className="hover:text-fg hover:underline"
          title={`Filtrar por "${categoryMap.get(p.categoryId) ?? 'categoría'}"`}
        >
          {categoryMap.get(p.categoryId) ?? '—'}
        </button>
      ) : (
        '—'
      )}
    </div>
    <div className="w-24 text-right font-bold">{formatCOP(p.priceCents)}</div>
    {canEdit || canDelete ? (
      <div className="flex w-24 justify-end gap-1">
        {canEdit ? (
          <Button asChild variant="ghost" size="sm">
            <Link to="/products/$productId" params={{ productId: p.id }}>
              <Pencil className="size-3.5" />
            </Link>
          </Button>
        ) : null}
        {canDelete ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDelete(p.id, p.name)}
            disabled={deleting}
          >
            <Trash2 className="size-3.5" />
          </Button>
        ) : null}
      </div>
    ) : null}
  </div>
);

// ---- Thumbnail ----

/**
 * Thumbnail clickeable: cuando el producto tiene imagen, se renderiza
 * dentro de un <button> que dispara el lightbox. Sin imagen, es un div
 * inerte — no habilitamos click sobre el placeholder porque no hay nada
 * que "ampliar".
 */
const ClickableThumbnail = ({
  url,
  alt,
  className,
  onClick,
}: {
  url: string | null;
  alt: string;
  className?: string | undefined;
  onClick: () => void;
}) => {
  if (!url) return <Thumbnail url={null} alt={alt} className={className} />;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Ver imagen ampliada: ${alt}`}
      className={cn(
        'block cursor-zoom-in overflow-hidden transition-opacity hover:opacity-85',
        className,
      )}
    >
      <Thumbnail url={url} alt={alt} className="size-full" />
    </button>
  );
};

const Thumbnail = ({
  url,
  alt,
  className,
}: {
  url: string | null;
  alt: string;
  className?: string | undefined;
}): ReactNode => {
  const resolved = resolveImageUrl(url);
  if (resolved) {
    return (
      <img
        src={resolved}
        alt={alt}
        className={cn('bg-surface-muted object-cover', className)}
        loading="lazy"
      />
    );
  }
  return (
    <div
      className={cn(
        'flex items-center justify-center bg-surface-muted text-fg-subtle',
        className,
      )}
      aria-label="Sin imagen"
    >
      <ImageOff className="size-5" />
    </div>
  );
};

// ---- ViewToggle ----

const ViewToggle = ({
  value,
  onChange,
}: {
  value: ViewMode;
  onChange: (v: ViewMode) => void;
}) => (
  <div
    className="flex items-center rounded-md border border-border bg-surface p-0.5"
    role="tablist"
  >
    <button
      type="button"
      role="tab"
      aria-selected={value === 'cards'}
      onClick={() => onChange('cards')}
      className={cn(
        'flex items-center gap-1.5 rounded-sm px-2.5 py-1 text-xs font-semibold tracking-tight transition-colors',
        value === 'cards'
          ? 'bg-fg text-fg-inverse'
          : 'text-fg-muted hover:text-fg',
      )}
    >
      <LayoutGrid className="size-3.5" /> Cards
    </button>
    <button
      type="button"
      role="tab"
      aria-selected={value === 'list'}
      onClick={() => onChange('list')}
      className={cn(
        'flex items-center gap-1.5 rounded-sm px-2.5 py-1 text-xs font-semibold tracking-tight transition-colors',
        value === 'list'
          ? 'bg-fg text-fg-inverse'
          : 'text-fg-muted hover:text-fg',
      )}
    >
      <List className="size-3.5" /> Lista
    </button>
  </div>
);
